import type { EventoDeBoleto } from "@/api/contrato";
import { dataHora } from "@/formato";

/*
 * A linha do tempo do boleto — doc 06 §4.2.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ É A ABA QUE RESPONDE "POR QUE ESSE BOLETO ESTÁ ASSIM".                   │
 * │                                                                          │
 * │ E o que responde não é a sequência de situações — é a ORIGEM de cada     │
 * │ transição. "Registrado por webhook" e "registrado por consulta           │
 * │ programada" contam histórias diferentes: a segunda significa que o       │
 * │ webhook não chegou, e isso é um defeito de integração esperando para     │
 * │ aparecer em outro boleto.                                                │
 * │                                                                          │
 * │ Por isso a origem tem tratamento visual próprio, e não é texto solto no  │
 * │ fim da linha.                                                            │
 * └──────────────────────────────────────────────────────────────────────────┘
 */

type Origem = "api" | "worker" | "webhook" | "polling" | "retorno_cnab" | "painel" | "auditoria";

/** O que cada origem quer dizer, em português de operador. */
const ROTULO: Record<Origem, string> = {
  api: "API",
  worker: "Worker",
  webhook: "Webhook",
  polling: "Consulta programada",
  retorno_cnab: "Retorno CNAB",
  painel: "Painel",
  auditoria: "Auditoria",
};

/*
 * `painel` é a única origem destacada, e de propósito: ela significa que uma
 * PESSOA mudou este boleto à mão. Quem investiga meses depois precisa ver isso
 * antes de tudo — e o nome de quem foi está em `auditoria_painel`.
 */
const ESTILO: Record<Origem, string> = {
  api: "border-fio-forte bg-superficie-2 text-texto-2",
  worker: "border-fio-forte bg-superficie-2 text-texto-2",
  webhook: "border-confirmado/25 bg-confirmado/12 text-confirmado",
  polling: "border-transito/25 bg-transito/12 text-transito",
  retorno_cnab: "border-fio-forte bg-superficie-2 text-texto-2",
  painel: "border-indeterminado/40 bg-indeterminado/12 text-indeterminado",
  auditoria: "border-fio-forte bg-superficie-2 text-texto-2",
};

export function LinhaDoTempo({ eventos }: { eventos: readonly EventoDeBoleto[] }) {
  if (eventos.length === 0) {
    // Uma linha curta, e nunca "Nenhum dado encontrado" (doc 06a §6).
    return <p className="px-4 py-6 text-texto-2">Nenhuma transição.</p>;
  }

  return (
    <ol className="flex flex-col px-4 py-3">
      {eventos.map((evento, indice) => {
        const origem = (evento.origem ?? "") as Origem;
        const conhecida = origem in ROTULO;

        return (
          <li key={`${evento.ocorrido_em}-${indice}`} className="relative flex gap-3 pb-4 last:pb-0">
            {/*
              O fio vertical que liga os marcadores. Não desenhado no último —
              uma linha que continua depois do fim sugere evento que não veio.
            */}
            {indice < eventos.length - 1 && (
              <span aria-hidden="true" className="absolute left-[5px] top-4 h-full w-px bg-fio" />
            )}

            <span
              aria-hidden="true"
              className="relative z-10 mt-[6px] size-[11px] shrink-0 rounded-full border-2 border-superficie bg-fio-forte"
            />

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-texto">
                  {evento.situacao_de && evento.situacao_de !== evento.situacao_para ? (
                    <>
                      <span className="text-texto-3">{evento.situacao_de}</span>
                      <span className="px-1 text-texto-3">→</span>
                    </>
                  ) : null}
                  <span className="font-medium">{evento.situacao_para}</span>
                </span>

                <span
                  className={`inline-flex items-center rounded-full border px-1.5 text-rotulo uppercase tracking-[0.08em] ${
                    conhecida ? ESTILO[origem] : "border-fio-forte bg-superficie-2 text-texto-2"
                  }`}
                  /*
                   * Origem desconhecida mostra o texto cru. O conjunto é aberto,
                   * como o das situações: uma origem nova precisa aparecer na
                   * tela em vez de sumir dela.
                   */
                >
                  {conhecida ? ROTULO[origem] : evento.origem}
                </span>
              </div>

              <div className="mt-[2px] flex flex-wrap items-center gap-2 text-[12px] text-texto-2">
                <span className="dados">{dataHora(evento.ocorrido_em)}</span>
                {evento.trace_id ? (
                  <span className="dados text-texto-3" title={evento.trace_id}>
                    {evento.trace_id.slice(0, 16)}
                  </span>
                ) : null}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
